import { camelCase } from 'es-toolkit';
import { LeaseSigningFlowStepNames } from 'models/enums';
import { LEASE_SIGNING_FLOW_STRINGS } from 'lease-signing-flow-v2/constants/strings';
import { LEASE_SIGNING_FLOW_STRINGS as STRINGS } from 'strings/lease-signing-flow.strings';
import { getString } from 'strings';
import { PATHS } from '@belong/common';

/**
 * `LEASE_SIGNING_FLOW_STRINGS` is a flat map of dotted keys, and only five of the
 * flow's steps declare a tip — so a key built from the step name misses for the
 * rest and reads `undefined`, which is why this returns an optional rather than
 * asserting the key exists. Two of the map's entries hold nested objects rather
 * than strings, hence the `typeof` narrowing; a `.tip.` key never reaches those.
 */
const getTipString = (key: string): string | undefined => {
  const value = (LEASE_SIGNING_FLOW_STRINGS as Record<string, unknown>)[key];
  return typeof value === 'string' ? value : undefined;
};

/**
 * Finds the step from the URL pathname
 */
export const findStepFromUrl = (pathname: string, availableSteps: any[]) => {
  if (!availableSteps || availableSteps.length === 0) return null;

  const pathSegments = pathname.split('/').filter(Boolean);
  // Never empty for the routes this runs under — the flow's own paths always
  // carry segments — and an empty pathname would already have thrown on the
  // `toLowerCase()` below rather than falling through to a no-match.
  const urlStepName = pathSegments[pathSegments.length - 1]!;

  const matchedStep = availableSteps.find(
    (step) => step.stepName.toLowerCase() === urlStepName.toLowerCase(),
  );

  return matchedStep || null;
};

/**
 * Gets the next step after the last completed step, or returns the current step
 * if it's the correct step to be on
 */
export const getNextAfterLastCompletedOrCurrent = (
  steps: any[],
  currentStepId: string,
) => {
  const visible = steps.filter((s) => !s.isHidden);

  // this logic handles sending the user to the first step that is not completed

  const urlParams = new URLSearchParams(window.location.search);
  const useLatestStep =
    urlParams.get('useLatestStep')?.toLowerCase() === 'true';

  if (useLatestStep) {
    // Return the first step that is not completed
    const firstNotCompleted = visible.find(
      (step) => step.status !== 'Completed',
    );

    if (
      firstNotCompleted &&
      firstNotCompleted.stepName &&
      firstNotCompleted.stepName !== LeaseSigningFlowStepNames.GetStarted
    ) {
      const currentUrl = window.location.pathname.toLowerCase();
      const stepNameInUrl = firstNotCompleted?.stepName?.toLowerCase();

      if (!currentUrl.includes(stepNameInUrl)) {
        window.location.href = `${window.location.origin}${PATHS.LEASE_SIGNING_FLOW_V2}/${firstNotCompleted.stepName}`;
      }
    }

    return firstNotCompleted || visible[visible.length - 1];
  }

  const idx = visible.findIndex((s) => s.id === currentStepId);
  if (idx === -1) return null;

  if (idx === 0) return visible[0];

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Legacy lint baseline; see docs/development/lint-baseline-followups.md. Refs: ARCH-56
  const prev = visible[idx - 1];

  // Check if useLatestStep query param is true

  // if we want to use the status of the previous step, we can use the following code
  // if (prev.status === 'Completed') {
  if (visible[idx]) {
    return visible[idx];
  }

  let lastCompletedIndex = -1;
  for (let i = idx - 1; i >= 0; i--) {
    if (visible[i].status === 'Completed') {
      lastCompletedIndex = i;
      break;
    }
  }

  if (lastCompletedIndex === -1) return visible[0];

  const nextAfter = lastCompletedIndex + 1;
  return visible[nextAfter] ?? null;
};

/**
 * Gets the sidebar tip for a given step
 */
export const getSidebarTip = (
  step: any,
  sidebarTip: any,
  home: any,
  lease: any,
) => {
  if (sidebarTip) return sidebarTip;

  console.log('step', step.name);

  if (step.name === LeaseSigningFlowStepNames.SignLease) {
    const tipDescription = STRINGS[`Sign.tips.description`];
    let description = tipDescription[home?.address?.state] || tipDescription.CA;

    if (lease?.leaseInfo.basicInfo.signatureInfo?.signMode === 'External') {
      description = tipDescription['WA-notarize'];
    }

    return {
      description: getString(description, {
        firstName: home?.owner?.name,
      }),
    };
  }

  const cameleCaseStepName = camelCase(step.name ?? '');

  return {
    title: getTipString(`${cameleCaseStepName}.tip.title`),
    description: getTipString(`${cameleCaseStepName}.tip.description`),
  };
};
